import { Box } from "@mui/system";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CreateCommonFolder from "./components/common/CreateCommonFolder";
import { STRINGS } from "./strings";
import { EMAIL_MARKETING_CAMPAIGN_PATH } from "../../utils/constants/routes";
import CustomHeader from "../../components/Header/CustomHeader";
import SearchField from "../../components/InputFields/SearchField";
import { ICON_LABELS, fontSize, fontWeight } from "../../utils/constants/UI";
import { Button } from "../../components/Buttons";
import CustomTable from "../../components/Table/CustomTable";
import {
  DEBOUNCE_DELAY,
  EMITTER_ERROR,
  EMITTER_SUCCESS,
  campaignHeadData,
} from "../../utils/constants";
import {
  Logger,
  debounceHelper,
  generateBreadCrumbDescription,
  getErrorMessage,
  processStatsKeyValuePairs,
} from "../../utils/helpers";
import { paginationDataAction } from "../../redux/actions/customTableActions";
import RecordDeleteModal from "./components/Sequence/RecordDeleteModal";
import { getRequest, postRequest } from "../../Apis";
import { campaignEndpoints } from "../../utils/constants/httpConstants";
import EventEmitter from "../../utils/emitter/EventEmitter";
import {
  EMITTER_CREDIT_WARNING,
  CREDIT_MODAL_TYPES,
} from "../../utils/creditMonitor/creditStatusCodes";
import CustomStyledMenu from "../../components/Menu/CustomStyledMenu";
import CustomDialog from "../../components/Dialog/CustomDialog";
import TypographyInline from "../../components/Typography/TypographyInline";
import FolderSkeletonLoader from "./components/common/FolderSkeletonLoader";
import IconComponent from "../../components/Icon/IconComponent";
import { GREY, WARNING } from "../../utils/constants/colors";
import { campaignsListFetch } from "../../redux/actions/campaignAction";
import CreateCampaignModal from "./components/Campaign/CreateCampaignModal";
import { StatsDialog } from "./components/Campaign";
import classes from "./marketing.module.css";
import RemoveFolder from "./components/common/RemoveFolder";
import EmptyScreen from "./EmptyScreen";
import FilterTab from "../../components/FilterTab/FilterTab";
import UserProfileDialog from "./components/Campaign/UserProfileDialog";
import { useTour } from "../../context/TourContext";
// useFirebaseProgressSync is called in DashboardLayout (app level) - not here

// REGENERATE HIDDEN PLATFORM-WIDE (2026-08-14, owner call). Kept as a single
// flag rather than deleting the feature so it is a one-line switch back on.
// Pairs with the same flag in ViewAiContentDialog.js (single-email Regenerate).
const HIDE_REGENERATE_CAMPAIGN = true;

const CAMPAIGN_TOUR_DUMMY_RECORDS = [
  { _id: "dummy_camp_1",  name: "Q2 Enterprise Outreach",         state: "Active",    from_email: "sarah@acme.com",       total_enrolled: 420, total_steps: 6, sent: 386, deliver: 371, lead: 18, bounce: 4, open: 142, meeting_book: 12, click: 38, reply: 27, unsubscribe: 3, created_at: "2025-05-10T09:00:00Z", owner: "Sarah Mitchell",  type: "file", user_id: "u1", cam_sequence_id: "seq_1",  cam_list_id: "list_1",  sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_2",  name: "SaaS Decision Makers Nurture",   state: "Active",    from_email: "daniel@acme.com",      total_enrolled: 310, total_steps: 5, sent: 298, deliver: 287, lead: 14, bounce: 2, open: 115, meeting_book: 9,  click: 29, reply: 21, unsubscribe: 1, created_at: "2025-05-18T11:30:00Z", owner: "Daniel Okafor",   type: "file", user_id: "u2", cam_sequence_id: "seq_2",  cam_list_id: "list_2",  sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_3",  name: "Mid-Market CFO Sequence",        state: "Completed", from_email: "priya@acme.com",       total_enrolled: 180, total_steps: 4, sent: 180, deliver: 174, lead: 22, bounce: 1, open: 98,  meeting_book: 17, click: 44, reply: 33, unsubscribe: 2, created_at: "2025-03-05T08:00:00Z", owner: "Priya Sharma",    type: "file", user_id: "u3", cam_sequence_id: "seq_3",  cam_list_id: "list_3",  sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_4",  name: "Inbound Follow-Up May",          state: "Paused",    from_email: "sarah@acme.com",       total_enrolled: 540, total_steps: 3, sent: 312, deliver: 300, lead: 8,  bounce: 6, open: 87,  meeting_book: 5,  click: 19, reply: 14, unsubscribe: 4, created_at: "2025-05-01T07:45:00Z", owner: "Sarah Mitchell",  type: "file", user_id: "u1", cam_sequence_id: "seq_4",  cam_list_id: "list_4",  sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_5",  name: "Healthcare VP Cold Outreach",    state: "Active",    from_email: "marcus@acme.com",      total_enrolled: 95,  total_steps: 7, sent: 88,  deliver: 85,  lead: 6,  bounce: 0, open: 41,  meeting_book: 4,  click: 12, reply: 9,  unsubscribe: 0, created_at: "2025-06-01T10:00:00Z", owner: "Marcus Reid",     type: "file", user_id: "u6", cam_sequence_id: "seq_5",  cam_list_id: "list_5",  sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_6",  name: "Series B Founders Campaign",     state: "Completed", from_email: "anjali@acme.com",      total_enrolled: 230, total_steps: 5, sent: 230, deliver: 221, lead: 31, bounce: 2, open: 133, meeting_book: 24, click: 56, reply: 44, unsubscribe: 1, created_at: "2025-02-14T13:00:00Z", owner: "Anjali Patel",    type: "file", user_id: "u7", cam_sequence_id: "seq_6",  cam_list_id: "list_6",  sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_7",  name: "EMEA Sales Director Push",       state: "Paused",    from_email: "james@acme.com",       total_enrolled: 175, total_steps: 4, sent: 140, deliver: 134, lead: 5,  bounce: 3, open: 52,  meeting_book: 3,  click: 11, reply: 8,  unsubscribe: 2, created_at: "2025-04-20T09:30:00Z", owner: "James Thornton",  type: "file", user_id: "u4", cam_sequence_id: "seq_7",  cam_list_id: "list_7",  sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_8",  name: "Re-Engagement Cold Leads",       state: "Active",    from_email: "nathan@acme.com",      total_enrolled: 380, total_steps: 6, sent: 355, deliver: 340, lead: 11, bounce: 5, open: 127, meeting_book: 8,  click: 24, reply: 19, unsubscribe: 3, created_at: "2025-05-28T14:15:00Z", owner: "Nathan Brooks",   type: "file", user_id: "u8", cam_sequence_id: "seq_8",  cam_list_id: "list_8",  sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_9",  name: "FinTech VP Prospecting",         state: "Active",    from_email: "lisa@acme.com",        total_enrolled: 260, total_steps: 5, sent: 241, deliver: 232, lead: 9,  bounce: 2, open: 88,  meeting_book: 6,  click: 18, reply: 13, unsubscribe: 1, created_at: "2025-04-08T10:15:00Z", owner: "Lisa Carvalho",   type: "file", user_id: "u5", cam_sequence_id: "seq_9",  cam_list_id: "list_9",  sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_10", name: "Webinar Attendee Follow-Up",     state: "Completed", from_email: "marcus@acme.com",      total_enrolled: 490, total_steps: 3, sent: 490, deliver: 472, lead: 27, bounce: 3, open: 196, meeting_book: 19, click: 63, reply: 41, unsubscribe: 5, created_at: "2025-01-22T08:00:00Z", owner: "Marcus Reid",     type: "file", user_id: "u6", cam_sequence_id: "seq_10", cam_list_id: "list_10", sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_11", name: "E-commerce CTO Sequence",        state: "Paused",    from_email: "nathan@acme.com",      total_enrolled: 140, total_steps: 6, sent: 98,  deliver: 94,  lead: 4,  bounce: 1, open: 37,  meeting_book: 2,  click: 8,  reply: 6,  unsubscribe: 0, created_at: "2025-03-30T12:00:00Z", owner: "Nathan Brooks",   type: "file", user_id: "u8", cam_sequence_id: "seq_11", cam_list_id: "list_11", sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_12", name: "ICP Matched AI Tools Push",      state: "Active",    from_email: "priya@acme.com",       total_enrolled: 320, total_steps: 5, sent: 307, deliver: 295, lead: 16, bounce: 2, open: 121, meeting_book: 11, click: 31, reply: 23, unsubscribe: 2, created_at: "2025-05-12T09:45:00Z", owner: "Priya Sharma",    type: "file", user_id: "u3", cam_sequence_id: "seq_12", cam_list_id: "list_12", sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_13", name: "Logistics Leaders Outreach",     state: "Completed", from_email: "james@acme.com",       total_enrolled: 210, total_steps: 4, sent: 210, deliver: 201, lead: 19, bounce: 2, open: 94,  meeting_book: 14, click: 37, reply: 28, unsubscribe: 1, created_at: "2025-02-28T11:00:00Z", owner: "James Thornton",  type: "file", user_id: "u4", cam_sequence_id: "seq_13", cam_list_id: "list_13", sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_14", name: "SMB Retail Owners Campaign",     state: "Active",    from_email: "anjali@acme.com",      total_enrolled: 155, total_steps: 5, sent: 143, deliver: 137, lead: 7,  bounce: 1, open: 54,  meeting_book: 5,  click: 14, reply: 10, unsubscribe: 1, created_at: "2025-05-22T13:30:00Z", owner: "Anjali Patel",    type: "file", user_id: "u7", cam_sequence_id: "seq_14", cam_list_id: "list_14", sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_15", name: "SaaStr Conference Follow-Up",    state: "Paused",    from_email: "daniel@acme.com",      total_enrolled: 280, total_steps: 4, sent: 195, deliver: 187, lead: 6,  bounce: 4, open: 71,  meeting_book: 4,  click: 15, reply: 11, unsubscribe: 3, created_at: "2025-04-25T10:00:00Z", owner: "Daniel Okafor",   type: "file", user_id: "u2", cam_sequence_id: "seq_15", cam_list_id: "list_15", sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_16", name: "APAC Enterprise Nurture",        state: "Active",    from_email: "lisa@acme.com",        total_enrolled: 190, total_steps: 6, sent: 174, deliver: 167, lead: 8,  bounce: 1, open: 66,  meeting_book: 6,  click: 17, reply: 12, unsubscribe: 0, created_at: "2025-05-30T08:30:00Z", owner: "Lisa Carvalho",   type: "file", user_id: "u5", cam_sequence_id: "seq_16", cam_list_id: "list_16", sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_17", name: "Product-Led Growth Segment",     state: "Completed", from_email: "sarah@acme.com",       total_enrolled: 360, total_steps: 5, sent: 360, deliver: 347, lead: 24, bounce: 3, open: 158, meeting_book: 18, click: 49, reply: 36, unsubscribe: 2, created_at: "2025-03-18T14:00:00Z", owner: "Sarah Mitchell",  type: "file", user_id: "u1", cam_sequence_id: "seq_17", cam_list_id: "list_17", sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_18", name: "Churned Customer Win-Back",      state: "Paused",    from_email: "marcus@acme.com",      total_enrolled: 120, total_steps: 4, sent: 88,  deliver: 84,  lead: 3,  bounce: 2, open: 31,  meeting_book: 2,  click: 7,  reply: 5,  unsubscribe: 1, created_at: "2025-04-14T09:00:00Z", owner: "Marcus Reid",     type: "file", user_id: "u6", cam_sequence_id: "seq_18", cam_list_id: "list_18", sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_19", name: "Demo Request Rapid Response",    state: "Active",    from_email: "daniel@acme.com",      total_enrolled: 78,  total_steps: 3, sent: 72,  deliver: 70,  lead: 9,  bounce: 0, open: 38,  meeting_book: 7,  click: 16, reply: 14, unsubscribe: 0, created_at: "2025-06-03T11:15:00Z", owner: "Daniel Okafor",   type: "file", user_id: "u2", cam_sequence_id: "seq_19", cam_list_id: "list_19", sequence_type: "automated", pin: 0, isIcpCampaign: false },
  { _id: "dummy_camp_20", name: "Enterprise AE Target List",      state: "Active",    from_email: "priya@acme.com",       total_enrolled: 470, total_steps: 7, sent: 431, deliver: 414, lead: 20, bounce: 5, open: 167, meeting_book: 15, click: 42, reply: 31, unsubscribe: 4, created_at: "2025-04-02T07:30:00Z", owner: "Priya Sharma",    type: "file", user_id: "u3", cam_sequence_id: "seq_20", cam_list_id: "list_20", sequence_type: "automated", pin: 0, isIcpCampaign: false },
];

const CampaignScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { activeTourKey } = useTour();
  const isCampaignsTourActive = activeTourKey === "campaigns" || activeTourKey === "dashboard";
  const getCampaignListSelector = useSelector(
    (state) => state?.campaignsListFetch,
  );
  const paginationSelector = useSelector((state) => state.getPaginationQuery);

  // The row-action handlers below are useCallback'd on [navigate, dispatch], so
  // reading paginationSelector directly inside them would capture the first
  // render's value. Adding it to their deps would also re-make the table's
  // useMemo on every page change, so mirror it into a ref instead.
  const paginationRef = useRef(paginationSelector?.data);
  useEffect(() => {
    paginationRef.current = paginationSelector?.data;
  }, [paginationSelector?.data]);

  // Refresh the list in place: same page, same search, same tab. A fresh query
  // object identity is what makes the list effect re-run, so spread it.
  const refreshListInPlace = useCallback(() => {
    dispatch(
      paginationDataAction({
        ...paginationRef.current,
        checkedRow: [],
        query: { ...paginationRef.current?.query },
      }),
    );
  }, [dispatch]);

  // The page number lived only in redux, so a browser refresh always landed on
  // page 1. Keep it in the URL instead - that also makes a list view shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSeededRef = useRef(false);

  useEffect(() => {
    if (pageSeededRef.current) return;
    pageSeededRef.current = true;
    const pageFromUrl = Number(searchParams.get("page")) || 1;
    if (pageFromUrl > 1) {
      dispatch(
        paginationDataAction({
          ...paginationRef.current,
          query: { ...paginationRef.current?.query, page: pageFromUrl },
        }),
      );
    }
  }, [dispatch, searchParams]);

  useEffect(() => {
    const page = Number(paginationSelector?.data?.query?.page) || 1;
    const pageInUrl = Number(searchParams.get("page")) || 1;
    if (page === pageInUrl) return;
    const next = new URLSearchParams(searchParams);
    if (page > 1) {
      next.set("page", String(page));
    } else {
      next.delete("page");
    }
    setSearchParams(next, { replace: true });
  }, [paginationSelector?.data?.query?.page, searchParams, setSearchParams]);
  const userDetailsSelector = useSelector((state) => state?.userDetails);
  const isAdminSDR = userDetailsSelector?.payload?.role_access === "admin-sdr";

  // Firebase progress from Redux - populated by useFirebaseProgressSync in DashboardLayout
  const firebaseProgress = useSelector((state) => state.firebaseProgress);
  // Ref to always access latest firebaseProgress inside async closures
  const firebaseProgressRef = useRef(firebaseProgress);
  firebaseProgressRef.current = firebaseProgress;
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [showFolderEdit, setShowFolderEdit] = useState(false);
  const [isOperationPermitted, setIsOperationPermitted] = useState(true);
  const [movedAnchorEl, setMovedAnchorEl] = useState(null);
  const [showRemoveFromFolder, setShowRemoveFromFolder] = useState(false);
  const [isMount, setIsMount] = useState(false);
  const [moving, setMoving] = useState(false);
  const [editFold, setEditFold] = useState({
    id: "",
    name: "",
  });
  const [resolvedRecords, setResolvedRecords] = useState([]);
  const [isActive, setIsActive] = useState("");
  const [operationList, setOperationList] = useState([
    {
      id: "pin",
      label: "add",
    },
    {
      id: "pin",
      label: "data",
    },
    {
      id: "pin",
      label: "list",
    },
  ]);
  const [moveFolderList, setMoveFolderList] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [showItemDelete, setShowItemDelete] = useState(false);
  const [regenConfirm, setRegenConfirm] = useState({ open: false, row: null });
  const [regenLoading, setRegenLoading] = useState(false);
  // const [showFolderEdit, setShowFolderEdit] = useState(false);
  const [operationRecordPayload, setOperationRecordPayload] = useState(null);
  const [statsShowModalOpen, setStatsModalShowOpen] = useState(false);
  const [statsPayload, setStatsPayload] = useState({});
  const [campaignPayload, setCampaignPayload] = useState({});
  const [search, setSearch] = useState("");
  const [currentTab, setCurrentTab] = useState("automated");
  const [headerItems, setHeaderItems] = useState([
    {
      id: STRINGS.TYPE_CAMP,
      label: STRINGS.CAMPAIGNS_TEXT,
      path: EMAIL_MARKETING_CAMPAIGN_PATH,
    },
  ]);
  const movedOpen = Boolean(movedAnchorEl);
  const [userEmail, setUserEmail] = useState("");
  const [openProfileDialog, setOpenProfileDialog] = useState(false);

  const handleSearch = () => {
    try {
      dispatch(
        paginationDataAction({
          ...paginationSelector?.data,
          checkedRow: [],
          query: {
            ...paginationSelector?.data?.query,
            search: search,
            sequence_type: currentTab,
            page: 1,
          },
        }),
      );
    } catch (error) {}
  };

  const handleFolderOpen = () => {
    setMovedAnchorEl(null);
    setFolderModalOpen(true);
  };

  const guardCreditsOrWarn = useCallback(() => {
    const availableCredits =
      userDetailsSelector?.payload?.available_credits ?? 0;
    if (availableCredits <= 0 || availableCredits <= 1) {
      EventEmitter.emit(EMITTER_CREDIT_WARNING, {
        modalType: CREDIT_MODAL_TYPES.ZERO_CREDITS,
        message:
          "You don't have any credits left. Please purchase more credits to create a campaign.",
      });
      return false;
    }
    return true;
  }, [userDetailsSelector]);

  const handleCampaignOpen = () => {
    if (!guardCreditsOrWarn()) return;
    setSequenceModalOpen(true);
  };

  const handleDeleteModalOpen = () => {
    setDeleteModalOpen(true);
  };

  const handleRemoveFromFolder = () => {
    setShowRemoveFromFolder(true);
  };

  const handleMovetoMenuOpenAnchor = (event) => {
    setMovedAnchorEl(event?.currentTarget);
  };

  const handleMovetoMenuOpen = async (event) => {
    try {
      setFoldersLoading(true);
      let userIdArr = getCampaignListSelector?.payload?.data?.records
        ?.filter((item) =>
          paginationSelector?.data?.checkedRow?.includes(item?._id),
        )
        ?.map((item) => {
          return item?.user_id;
        });
      const payload = {
        owner_ids:
          userDetailsSelector?.payload?.role === STRINGS.ADMIN_USER
            ? userIdArr
            : [],
      };

      const response = await postRequest(
        navigate,
        campaignEndpoints.getMoveList,
        payload,
      );
      setMoveFolderList(response?.data?.data?.data || []);
    } catch (error) {
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
    } finally {
      setFoldersLoading(false);
    }
  };

  const handleClose = () => {
    setMovedAnchorEl(null);
  };

  const backHandle = (id) => {
    setIsActive(id);
  };

  const handleFolderSearch = async (event) => {
    try {
      setFoldersLoading(true);
      let userIdArr = getCampaignListSelector?.payload?.data?.records
        ?.filter((item) =>
          paginationSelector?.data?.checkedRow?.includes(item?._id),
        )
        ?.map((item) => {
          return item?.user_id;
        });
      const payload = {
        owner_ids:
          userDetailsSelector?.payload?.role === STRINGS.ADMIN_USER
            ? userIdArr
            : [],
        search: event.target.value,
      };

      const response = await postRequest(
        navigate,
        campaignEndpoints.getMoveList,
        payload,
      );
      setMoveFolderList(response?.data?.data?.data || []);
    } catch (error) {
    } finally {
      setFoldersLoading(false);
    }
  };

  const handleDebounceFolderSearch = debounceHelper(
    handleFolderSearch,
    DEBOUNCE_DELAY,
  );

  const moveCampaign = async (id) => {
    try {
      const payload = {
        campaign_ids: paginationSelector?.data?.checkedRow,
        folder_id: id,
      };

      setMoving(true);
      const response = await postRequest(
        navigate,
        campaignEndpoints.moveCampaignToList,
        payload,
      );
      navigate(`${EMAIL_MARKETING_CAMPAIGN_PATH}/${id}`);
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
      setIsActive("");
      setMovedAnchorEl(null);
      // dispatch(paginationDataAction({ checkedRow: [], query: {} }));
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(err?.data?.data?.errors || {}, err?.data?.message),
      );
    } finally {
      setMoving(false);
    }
  };

  const handlePinRecord = useCallback(
    async (row) => {
      try {
        let type;
        row.type == "file" ? (type = "file") : (type = "folder");
        const new_pin = row?.pin === 0 ? 1 : 0;
        const payload = { pin_unpin: new_pin, record_id: row?._id, type };
        const response = await postRequest(
          navigate,
          campaignEndpoints.pinUnpinCampaign,
          payload,
        );
        EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
        refreshListInPlace();
      } catch (error) {
        EventEmitter.emit(
          EMITTER_ERROR,
          getErrorMessage(
            error?.data?.data?.errors || {},
            error?.data?.message,
          ),
        );
      }
    },
    [navigate, refreshListInPlace],
  );

  const handleEditCampaign = useCallback(async (row) => {
    setEditFold({
      id: row?._id,
      name: row?.name,
    });
    setShowFolderEdit(true);
  }, []);

  const value = {
    create: STRINGS.CRT_CMPGS,
    content: STRINGS.CMPGS_CONT,
    body: [STRINGS.CMP_BD1, STRINGS.CMP_BD2, STRINGS.CMP_BD3],
    foot: STRINGS.CRT_CMPGS,
  };

  const handlePauseCampaign = useCallback(
    async (row) => {
      try {
        const pause_response = await getRequest(
          navigate,
          campaignEndpoints.puaseCampaign + "/" + row?._id,
        );
        EventEmitter.emit(EMITTER_SUCCESS, pause_response?.data?.message);
        refreshListInPlace();
      } catch (error) {
        EventEmitter.emit(
          EMITTER_ERROR,
          getErrorMessage(
            error?.data?.data?.errors || {},
            error?.data?.message,
          ),
        );
      }
    },
    [navigate, refreshListInPlace],
  );

  const handleResumeCampaign = useCallback(
    async (row) => {
      try {
        const pause_response = await getRequest(
          navigate,
          campaignEndpoints.resumeCampaign + "/" + row?._id,
        );
        EventEmitter.emit(EMITTER_SUCCESS, pause_response?.data?.message);
        refreshListInPlace();
      } catch (error) {
        EventEmitter.emit(
          EMITTER_ERROR,
          getErrorMessage(
            error?.data?.data?.errors || {},
            error?.data?.message,
          ),
        );
      }
    },
    [navigate, refreshListInPlace],
  );

  const handleShowItemDelete = useCallback((row) => {
    try {
      setShowItemDelete(true);
      setOperationRecordPayload(row);
    } catch (error) {}
  }, []);

  // REGENERATE CAMPAIGN (2026-08-13): three-dot option on Ready To Run
  // prompt-based campaigns — confirm first, then the whole campaign goes back
  // through the same personalization flow as first-time creation (state flips
  // to Analyzing Data, existing progress UI/Firebase take over from there).
  const handleShowRegenerate = useCallback((row) => {
    setRegenConfirm({ open: true, row });
  }, []);

  const confirmRegenerateCampaign = async () => {
    const row = regenConfirm.row;
    if (!row?._id || regenLoading) return;
    try {
      setRegenLoading(true);
      const response = await postRequest(
        navigate,
        campaignEndpoints.regenerateCampaign,
        { campaign_id: row._id },
      );
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
      setRegenConfirm({ open: false, row: null });
      dispatch(paginationDataAction({ query: {}, checkedRow: [] }));
    } catch (error) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(
          error?.data?.data?.errors || {},
          error?.data?.message,
        ),
      );
    } finally {
      setRegenLoading(false);
    }
  };

  const isBulkOperationAllowed = (type = STRINGS.FILE) => {
    try {
      if (paginationSelector?.data?.checkedRow?.length) {
        // check if only one type exists.
        const records = getCampaignListSelector?.payload?.data?.records || [];
        const newRecords = [
          ...new Set(
            records
              .filter((item) =>
                paginationSelector?.data?.checkedRow?.includes(
                  item._id || item?.id,
                ),
              )
              ?.map((item) => item?.type),
          ),
        ];
        if (newRecords?.length >= 2) {
          return false;
        }
        if (
          type === STRINGS.SMALL_FOLDER &&
          newRecords.includes(STRINGS.SMALL_FOLDER)
        ) {
          return false;
        }
        return true;
      }
      return false;
    } catch (error) {
      Logger(error);
      return false;
    }
  };

  const handleeViewStatModal = useCallback((row, head) => {
    try {
      if (row?.type === STRINGS.FILE) {
        setStatsModalShowOpen(true);
        setStatsPayload({ row: row, head: head });
      }
    } catch (error) {
      EventEmitter.emit(EMITTER_ERROR, error.message);
    }
  }, []);

  const showProfileHandle = (email) => {
    setUserEmail(email);
    setOpenProfileDialog(true);
  };

  const handleCloneCampaign = useCallback(
    (row) => {
      if (!guardCreditsOrWarn()) return;
      setCampaignPayload(row);
      setSequenceModalOpen(true);
    },
    [guardCreditsOrWarn],
  );

  const filteredRecords = resolvedRecords;

  // const manualCount =
  //   getCampaignListSelector?.payload?.data?.counts?.manual || 0;
  const automatedCount =
    getCampaignListSelector?.payload?.data?.counts?.automated || 0;

  const handleUserInfoAction = useCallback(
    (a) => {
      dispatch(paginationDataAction({}));

      if (a.type === STRINGS.FILE) {
        const statusNormalized =
          a.state?.toLowerCase()?.replace(/ /g, "_") || "";
        const seqTypeLower = a.sequence_type?.toLowerCase() || "";
        const reason = a.interruption_reason || a.reason;
        // Only true when backend explicitly sets isIcpCampaign - tenantPrompt alone is NOT ICP
        const isIcpCampaign = a.isIcpCampaign === true;

        const isIcpFlowState = [
          "icp_verifying",
          "icp_verified",
          "icp_matching",
          "personalizing",
          "personalizing_sequence",
          "ai_processing",
          "analyzing_data",
        ].includes(statusNormalized);

        if (isIcpCampaign && isIcpFlowState) {
          navigate(`${EMAIL_MARKETING_CAMPAIGN_PATH}/${a?._id}/icp-review`);
          return;
        }

        const isInterrupted = statusNormalized === "interrupted";

        if (isInterrupted) {
          EventEmitter.emit(
            EMITTER_ERROR,
            `Your campaign got interrupted. ${reason || "Please check your settings or contact support to resume."}`,
          );
          return;
        }

        const PATH =
          EMAIL_MARKETING_CAMPAIGN_PATH +
          "/" +
          a?._id +
          "&" +
          a?.cam_sequence_id +
          "&" +
          a?.cam_list_id +
          "&" +
          a?.user_id +
          "/" +
          STRINGS.CAMPAIGN_CONTACTS_ID;
        navigate(PATH);
      } else {
        navigate(`${EMAIL_MARKETING_CAMPAIGN_PATH}/${a?._id}`);
      }
    },
    [dispatch, navigate],
  );

  const handleRowAction = useCallback(
    (a) => {
      if (a.type === STRINGS.FILE) {
        const statusNormalized =
          a.state?.toLowerCase()?.replace(/ /g, "_") || "";
        const seqTypeLower = a.sequence_type?.toLowerCase() || "";

        const isManual = seqTypeLower === "manual";
        const isAutomated = seqTypeLower === "automated";
        const isTenantPrompt = seqTypeLower === "tenantprompt";

        const canPauseManual =
          isManual && ["scheduled", "active"].includes(statusNormalized);
        const canPauseAutomated =
          isAutomated && ["scheduled", "active"].includes(statusNormalized);
        const canPauseTenantPrompt =
          isTenantPrompt &&
          ["scheduled", "active"].includes(statusNormalized);
        const canPause =
          canPauseManual || canPauseAutomated || canPauseTenantPrompt;
        const canResume = statusNormalized === "paused";
        // Only prompt-based campaigns that are fully generated but not yet
        // scheduled — the only state where rows are safe to rewrite in place.
        // HIDDEN FOR EVERY TENANT (2026-08-14, owner call): the whole-campaign
        // Regenerate option is switched off platform-wide. The handler and the
        // confirm dialog below are left intact so this is a one-line revert —
        // flip HIDE_REGENERATE_CAMPAIGN back to false to bring it back.
        const canRegenerate =
          !HIDE_REGENERATE_CAMPAIGN &&
          isTenantPrompt &&
          statusNormalized === "ready_to_run";

        const baseOperations = [
          {
            id: STRINGS.PIN,
            label: STRINGS.COMMON_PIN,
            iconLabel: ICON_LABELS.PUSH_PIN,
            function: handlePinRecord,
          },
          {
            id: STRINGS.PAUSE,
            label: STRINGS.PAUSE,
            iconLabel: ICON_LABELS.PAUSE,
            function: handlePauseCampaign,
            hidden: !canPause,
          },
          {
            id: STRINGS.RESUME,
            label: STRINGS.RESUME,
            iconLabel: ICON_LABELS.PLAY_ARROW,
            function: handleResumeCampaign,
            hidden: !canResume,
          },
          {
            id: STRINGS.CLONE,
            label: STRINGS.CLONE,
            iconLabel: ICON_LABELS.CONTENT_COPY_1,
            function: handleCloneCampaign,
          },
          {
            id: "regenerate",
            label: "Regenerate",
            iconLabel: ICON_LABELS.REFRESH,
            function: handleShowRegenerate,
            hidden: !canRegenerate,
          },
          {
            id: STRINGS.SMALL_DELETE,
            label: STRINGS.DELETE,
            iconLabel: ICON_LABELS.DELETE,
            variant: "error",
            function: handleShowItemDelete,
          },
        ];

        setOperationList(baseOperations.filter((op) => !op.hidden));
      } else {
        setOperationList([
          {
            id: STRINGS.PIN,
            label: STRINGS.COMMON_PIN,
            iconLabel: ICON_LABELS.PUSH_PIN,
            function: handlePinRecord,
          },
          {
            id: STRINGS.COMMON_EDIT,
            label: STRINGS.COMMON_EDIT,
            iconLabel: ICON_LABELS.EDIT,
            function: handleEditCampaign,
          },
          {
            id: STRINGS.SMALL_DELETE,
            label: STRINGS.DELETE,
            iconLabel: ICON_LABELS.DELETE,
            variant: "error",
            function: handleShowItemDelete,
          },
        ]);
      }
    },
    [
      handlePinRecord,
      handlePauseCampaign,
      handleResumeCampaign,
      handleCloneCampaign,
      handleShowItemDelete,
      handleShowRegenerate,
      handleEditCampaign,
    ],
  );

  const campaignEmailTableData = useMemo(
    () => ({
    heads: campaignHeadData,
    body: isCampaignsTourActive ? CAMPAIGN_TOUR_DUMMY_RECORDS : filteredRecords,
    isSticky: true,
    operations: operationList || [
      {
        id: STRINGS.PIN,
        label: STRINGS.COMMON_PIN,
        iconLabel: ICON_LABELS.PUSH_PIN,
        function: handlePinRecord,
      },
      {
        id: STRINGS.PAUSE,
        label: STRINGS.PAUSE,
        iconLabel: ICON_LABELS.PAUSE,
        function: handlePauseCampaign,
      },
      {
        id: STRINGS.COMMON_EDIT,
        label: STRINGS.COMMON_EDIT,
        iconLabel: ICON_LABELS.EDIT,
        function: handlePauseCampaign,
      },
      {
        id: STRINGS.RESUME,
        label: STRINGS.RESUME,
        iconLabel: ICON_LABELS.PLAY_ARROW,
        function: handleResumeCampaign,
      },
      {
        id: STRINGS.CLONE,
        label: STRINGS.CLONE,
        iconLabel: ICON_LABELS.CONTENT_COPY_1,
        function: handleCloneCampaign,
      },
      {
        id: STRINGS.SMALL_DELETE,
        label: STRINGS.DELETE,
        iconLabel: ICON_LABELS.DELETE,
        variant: "error",
        function: handleShowItemDelete,
      },
    ],

    // heads for loading in particular column.
    loading_heads: [],
    actions: {
      // marketing_list: {
      user_info: {
        function: handleUserInfoAction,
        loading: true,
      },
      action: {
        function: handleRowAction,
      },
      stats_data: {
        function: handleeViewStatModal,
      },
    },
    sortHandler: {},
    total_records: isCampaignsTourActive ? CAMPAIGN_TOUR_DUMMY_RECORDS.length : getCampaignListSelector?.payload?.total_records,
    isLoading: isCampaignsTourActive ? false : getCampaignListSelector?.loading,
    disable_key: STRINGS.DISABLE_KEY,
    }),
    [
      filteredRecords,
      operationList,
      handlePinRecord,
      handlePauseCampaign,
      handleResumeCampaign,
      handleCloneCampaign,
      handleShowItemDelete,
      handleUserInfoAction,
      handleRowAction,
      handleeViewStatModal,
      getCampaignListSelector?.payload?.total_records,
      getCampaignListSelector?.loading,
      isCampaignsTourActive,
    ],
  );

  const moveToMenuContent = (
    <Box className={classes.movingContainer}>
      <Box className={classes.movingHeader}>
        <Box className={classes.moveStyle}>
          <TypographyInline
            size={"md"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.MOVE_TO_FOLDER}
          />
        </Box>
      </Box>
      <Box className={classes.moveStyle1}>
        <SearchField
          autoComplete="off"
          size={"sm36"}
          placeholder={STRINGS.SEARCH_FOLDER}
          onChange={handleDebounceFolderSearch}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </Box>
      <Box className={classes.moveDataHeight}>
        {foldersLoading ? (
          <Box className={classes.containSkeleton}>
            <FolderSkeletonLoader />
          </Box>
        ) : (
          moveFolderList?.map((val, id) => {
            return (
              <Box
                className={
                  isActive === val._id
                    ? classes.activeMovingContent
                    : classes.movingContent
                }
                onClick={() => backHandle(val._id)}
              >
                <IconComponent
                  color={WARNING.W_500}
                  fontSize={fontSize.LG}
                  iconLabel={ICON_LABELS.FOLDER}
                />
                <Box
                  className={
                    isActive === val._id
                      ? classes.movDataCont1
                      : classes.movDataCont
                  }
                >
                  <TypographyInline
                    size={"sm"}
                    color={GREY.SECONDARY}
                    fontWeight={fontWeight.REGULAR}
                    label={val.name}
                  />
                </Box>
              </Box>
            );
          })
        )}
      </Box>
      <Box className={classes.movFooter}>
        {!isAdminSDR && (
          <Box>
            <Button
              label={STRINGS.CREATE_FOLDER}
              LeftIcon={ICON_LABELS.ADD}
              size={"sm36"}
              variant="text"
              onClick={handleFolderOpen}
            />
          </Box>
        )}
        <Box className={classes.movBtnGen}>
          <Button
            label={STRINGS.COMMON_CANCEL}
            variant={"ghost"}
            size={"sm36"}
            onClick={handleClose}
          />
          <Button
            label={STRINGS.MOVE}
            size={"sm36"}
            variant={"primary"}
            onClick={() => moveCampaign(isActive)}
            disabled={!isActive}
            isLoading={moving}
          />
        </Box>
      </Box>
    </Box>
  );

  const getTypeFromIds = () => {
    const type = getCampaignListSelector?.payload?.data?.records?.filter(
      (item) =>
        paginationSelector?.data?.checkedRow?.includes(item?._id || item?.id),
    )?.[0]?.type;
    return type;
  };

  useEffect(() => {
    if (!statsShowModalOpen) {
      (async () => {
        try {
          const payload = {
            limit: paginationSelector?.data?.query?.limit || 25,
            ...paginationSelector?.data?.query,
            sequence_type: currentTab,
          };
          if (id?.length) {
            payload.folder_id = id;
          }
          await dispatch(campaignsListFetch(payload, navigate));
        } catch (err) {}
      })();
      // get folder details api.
      if (id) {
        (async () => {
          try {
            const get_folder_details_payload = await getRequest(
              navigate,
              `${campaignEndpoints.getInfo}/${id}`,
            );
            // condition to not add new header item on every re-render.
            const route_length = window.location.pathname
              ?.split(EMAIL_MARKETING_CAMPAIGN_PATH)?.[1]
              ?.split("/");
            let new_header_items;
            headerItems?.length > 1 && headerItems.pop();
            new_header_items = [
              ...headerItems,
              {
                id: id,
                label: get_folder_details_payload?.data?.data?.name || "",
              },
            ];
            if (headerItems?.length !== route_length?.length + 1) {
              setHeaderItems(new_header_items);
            }
            setIsOperationPermitted(
              get_folder_details_payload?.data?.data?.operation,
            );
          } catch (error) {
            EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
          }
        })();
      } else {
        setHeaderItems([
          {
            id: STRINGS.TYPE_CAMP,
            label: STRINGS.CAMPAIGNS_TEXT,
            path: EMAIL_MARKETING_CAMPAIGN_PATH,
          },
        ]);
      }
    }
  }, [dispatch, navigate, paginationSelector?.data?.query, currentTab, id]);

  // Pipeline state priority - higher index = more advanced state
  // Order reflects the full ICP+Automated flow so state never regresses
  const STATE_PRIORITY = [
    "entry",
    "setting_up",
    "icp_matching",
    "icp_verifying",
    "icp_verified",
    "analyzing_data",
    "analyzing",
    "personalizing",
    "personalizing_sequence",
    "ai_processing",
    "ai_generating_response",
    "personalizing_batch",
    "ready_to_run",
    "scheduled",
    "active",
    "paused",
    "completed",
    "interrupted",
  ];

  const getStatePriority = (state) => {
    const idx = STATE_PRIORITY.indexOf(
      (state || "").toLowerCase().replace(/ /g, "_"),
    );
    return idx === -1 ? -1 : idx;
  };

  // Display-friendly labels for Firebase states
  const icpStateToDisplay = {
    icp_verifying: "ICP Matching",
    icp_verified: "ICP Verified",
  };
  const campaignStateToDisplay = {
    analyzing: "Analyzing Data",
    personalizing: "Personalizing",
    personalizing_batch: "Personalizing",
    ready_to_run: "Ready To Run",
  };

  // Helper: merge Firebase progress from Redux into a record
  const mergeFirebaseIntoRecord = (rec, fbProgress) => {
    const fbEntry = fbProgress[rec._id];
    if (!fbEntry) return rec;

    const icpData = fbEntry.icp;
    const campData = fbEntry.campaign;

    const apiPriority = getStatePriority(rec.state);
    let bestState = rec.state;
    let bestMessage = rec.state_message || "";
    let bestProgress = null;
    let bestPriority = apiPriority;

    // campaign_progress (analyzing, personalizing, ready_to_run) - advance or match
    if (campData && campData.state) {
      const campDisplay = campaignStateToDisplay[campData.state];
      const campPriority = getStatePriority(campData.state);
      if (campDisplay && campPriority >= bestPriority) {
        bestState = campDisplay;
        bestMessage = `${campData.completed || 0} of ${campData.total || 0} contacts personalized (${campData.percentage || 0}%)`;
        bestProgress = campData.state === "ready_to_run" ? null : campData;
        bestPriority = campPriority;
      }
    }

    // icp_progress (icp_verifying, icp_verified) - advance or match
    // Uses >= so icp_verifying with percentage can update when API is at a lower/equal state
    if (icpData && icpData.state) {
      const icpDisplay = icpStateToDisplay[icpData.state];
      const icpPriority = getStatePriority(icpData.state);
      if (icpDisplay && icpPriority >= bestPriority) {
        bestState = icpDisplay;
        bestMessage = `${icpData.completed || 0} of ${icpData.total || 0} contacts verified (${icpData.percentage || 0}%)`;
        bestProgress = icpData.state === "icp_verified" ? null : icpData;
        bestPriority = icpPriority;
      }
    }

    // Identity preservation: if nothing the row displays actually changed,
    // return the SAME rec reference. EFFECT 2 maps every row on every Firebase
    // tick, so without this each tick allocates a fresh array of fresh objects
    // and re-renders CampaignScreen (and the heavy CustomTable) for nothing.
    // Compare _firebaseProgress by value (it's a fresh object from Redux each
    // tick) rather than by reference, else this early-return would rarely fire.
    const prevProgress = rec._firebaseProgress || null;
    const sameProgress =
      prevProgress === bestProgress ||
      (prevProgress &&
        bestProgress &&
        prevProgress.state === bestProgress.state &&
        prevProgress.percentage === bestProgress.percentage &&
        prevProgress.completed === bestProgress.completed &&
        prevProgress.total === bestProgress.total);
    if (
      rec.state === bestState &&
      (rec.state_message || "") === bestMessage &&
      sameProgress
    ) {
      return rec;
    }

    return {
      ...rec,
      state: bestState,
      state_message: bestMessage,
      _firebaseProgress: bestProgress,
    };
  };

  // EFFECT 1: Fetch stats + owners when campaign list changes
  // Step 1: Immediately set records from API (no "No Records Found" flash)
  // Step 2: Async fetch stats/owners, then merge with Firebase
  useEffect(() => {
    const apiRecords = getCampaignListSelector?.payload?.data?.records || [];
    if (!apiRecords.length) return;

    // Immediately populate table with basic records + Firebase merge (no stats yet)
    const immediateRecords = apiRecords.map((item) =>
      mergeFirebaseIntoRecord(item, firebaseProgressRef.current),
    );
    setResolvedRecords(immediateRecords);

    // Then async fetch stats and owners
    (async () => {
      try {
        const campaign_ids = apiRecords.map((item) => item?._id);
        const owner_ids = apiRecords.map((item) => item?.user_id);
        const promises = [
          postRequest(navigate, campaignEndpoints.campStats, {
            campaign_ids: campaign_ids,
          }),
          postRequest(navigate, campaignEndpoints.campOwnerStats, {
            user_ids: owner_ids,
          }),
        ];
        const resolved_promises = await Promise.allSettled(promises);

        const stats_payload = resolved_promises[0]?.value?.data;
        const owner_payload = resolved_promises[1]?.value?.data;

        const structured_records = apiRecords.map((item) => {
          const rec = {
            ...item,
            owner: owner_payload?.data?.userdata?.[item?.user_id],
            ...processStatsKeyValuePairs(
              stats_payload?.records[item?._id] || {},
            ),
          };
          return mergeFirebaseIntoRecord(rec, firebaseProgressRef.current);
        });

        setResolvedRecords(structured_records);
      } catch (error) {
        Logger(error);
      }
    })();
  }, [getCampaignListSelector?.payload]);

  // EFFECT 2: Merge Firebase progress on every Redux change (no API calls, instant)
  useEffect(() => {
    setResolvedRecords((prev) => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev.map((rec) => {
        const merged = mergeFirebaseIntoRecord(rec, firebaseProgress);
        if (merged !== rec) changed = true;
        return merged;
      });
      // No row actually changed -> keep the prior array reference so this
      // setState bails out (React skips the re-render) instead of allocating a
      // new array identity on every tick.
      return changed ? next : prev;
    });
  }, [firebaseProgress]);

  // debounce search api call for search
  useEffect(() => {
    if (isMount) {
      let getData;
      getData = setTimeout(() => {
        handleSearch();
      }, 300);
      return () => clearTimeout(getData);
    } else {
      setIsMount(true);
    }
  }, [search]);

  useEffect(() => {
    setSearch("");
    setIsMount(false);
  }, [navigate]);

  return (
    <Box className={classes.container}>
      {/* Common Header */}
      <CustomHeader
        items={headerItems}
        description={
          getCampaignListSelector?.loading
            ? STRINGS.LOADING
            : generateBreadCrumbDescription(getCampaignListSelector?.payload)
        }
      />

      {/* Bulk Operations Header */}
      {!isCampaignsTourActive && getCampaignListSelector?.payload?.is_empty ? (
        <EmptyScreen
          data={value}
          handleOpen={handleCampaignOpen}
          handleFolder={handleFolderOpen}
          isAdminSDR={isAdminSDR}
        />
      ) : (
        <>
          {/* <Box
            className={classes.tabContainer}
            style={{
              margin: "10px 1rem 0 1rem",
              borderRadius: "6px 6px 0 0",
              borderBottom: "1px solid #efefef",
              padding: "10px 16px",
            }}
          >
            <FilterTab
              label="AI-Powered"
              isActive={currentTab === "automated"}
              onClick={() => {
                setCurrentTab("automated");
                dispatch(
                  paginationDataAction({
                    ...paginationSelector?.data,
                    checkedRow: [],
                    query: {
                      ...paginationSelector?.data?.query,
                      sequence_type: "automated",
                      page: 1,
                    },
                  }),
                );
              }}
              selectedValue={automatedCount}
            /> */}
            {/* Pipe separator */}
            {/* <span
              style={{
                display: "inline-block",
                width: "1px",
                height: "20px",
                background: "#E0E0E0",
                margin: "0 6px",
                alignSelf: "center",
              }}
            />
            <FilterTab
              label="Automated"
              isActive={currentTab === "manual"}
              onClick={() => {
                setCurrentTab("manual");
                dispatch(
                  paginationDataAction({
                    ...paginationSelector?.data,
                    checkedRow: [],
                    query: {
                      ...paginationSelector?.data?.query,
                      sequence_type: "manual",
                      page: 1,
                    },
                  }),
                );
              }}
              selectedValue={manualCount}
            /> */}
          {/* </Box> */}
          <Box className={classes.buttonscContainer}>
            <Box className={classes.leftHeadContainer}>
              <Box className={classes.commonWidth}>
                <SearchField
                  size={"sm36"}
                  autoComplete="off"
                  placeholder={STRINGS.SEARCH}
                  value={search}
                  onChange={(event) => {
                    setSearch(event?.target?.value);
                  }}
                />
              </Box>
              {!isBulkOperationAllowed(STRINGS.SMALL_FOLDER) ? null : (
                <Box onClick={handleMovetoMenuOpen}>
                  <Button
                    label={STRINGS.MOVE_TO}
                    LeftIcon={ICON_LABELS.DRIVE_FILE_MOVE}
                    variant={"ghost"}
                    size={"sm36"}
                    onClick={handleMovetoMenuOpenAnchor}
                    disabled={!isBulkOperationAllowed(STRINGS.SMALL_FOLDER)}
                  />
                </Box>
              )}
              {isBulkOperationAllowed() && (
                <Box>
                  <Button
                    label={STRINGS.DELETE}
                    LeftIcon={ICON_LABELS.DELETE}
                    variant={"ghost"}
                    disabled={!paginationSelector?.data?.checkedRow?.length}
                    size={"sm36"}
                    onClick={handleDeleteModalOpen}
                  />
                </Box>
              )}
              {id?.length && isBulkOperationAllowed() && (
                <Box>
                  <Button
                    label={STRINGS.RM_FROM_FOLDER}
                    variant={"ghost"}
                    disabled={!paginationSelector?.data?.checkedRow?.length}
                    size={"sm36"}
                    onClick={handleRemoveFromFolder}
                  />
                </Box>
              )}
            </Box>
            <Box className={classes.rightHeadContainer}>
              {!isAdminSDR && (
                <>
                  {!id?.length && (
                    <Box>
                      <Button
                        label={STRINGS.CREATE_FOLDER}
                        LeftIcon={ICON_LABELS.CREATE_NEW_FOLDER}
                        variant={"ghost"}
                        size={"sm36"}
                        onClick={handleFolderOpen}
                      />
                    </Box>
                  )}
                  <Box data-tour="create-campaign-btn">
                    <Button
                      disabled={id?.length && !isOperationPermitted}
                      label={STRINGS.CREATE_CAMPAIGN}
                      LeftIcon={ICON_LABELS.ADD}
                      variant={"primary"}
                      size={"sm36"}
                      onClick={handleCampaignOpen}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Box>
          {/* Bulk Operations Header */}

          <Box className={classes.searchAndTableContainer}>
            <div className={classes.tableStyle}>
              <CustomTable tableData={campaignEmailTableData} />
            </div>
          </Box>
        </>
      )}
      {/* Custom Table Rendeering */}

      {/* All Menu's */}

      <CustomStyledMenu
        width="20.208vw"
        anchorEl={movedAnchorEl}
        open={movedOpen}
        onClose={handleClose}
      >
        {moveToMenuContent}
      </CustomStyledMenu>

      {/* All Menu's */}

      {/* All Modals */}

      {/* Create Folder Modal */}
      {folderModalOpen && (
        <CreateCommonFolder
          open={folderModalOpen}
          setOpen={setFolderModalOpen}
          type={STRINGS.TYPE_CAMP}
          endFunction={handleMovetoMenuOpen}
        />
      )}

      {/* Delete Sequence Modal */}
      {deleteModalOpen && (
        <RecordDeleteModal
          open={true}
          setOpen={setDeleteModalOpen}
          IDs={paginationSelector?.data?.checkedRow || []}
          val={STRINGS.CAMPAIGN_TEXT}
          itemType={getTypeFromIds()}
          endPoint={campaignEndpoints.deleteMany}
        />
      )}

      {/* Delete from operation Modal */}
      {showItemDelete && (
        <RecordDeleteModal
          open={true}
          setOpen={setShowItemDelete}
          IDs={[operationRecordPayload?._id] || []}
          val={STRINGS.CAMPAIGN_TEXT}
          itemName={operationRecordPayload?.name}
          itemType={operationRecordPayload?.type}
          endPoint={campaignEndpoints.deleteMany}
        />
      )}
      {/* Delete from operation Modal */}

      {/* REGENERATE CAMPAIGN confirm (Aditya): Ready To Run only — whole
          campaign is re-personalized with the same prompt/list/model */}
      {regenConfirm.open && (
        <CustomDialog
          open={regenConfirm.open}
          onClose={() =>
            !regenLoading && setRegenConfirm({ open: false, row: null })
          }
          width="440px"
          header={{
            heading: {
              label: "Regenerate campaign",
            },
          }}
        >
          <Box p={1}>
            <TypographyInline
              size={"sm"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.REGULAR}
              label={`Are you sure you want to regenerate "${regenConfirm.row?.name || "this campaign"}"? All emails will be re-personalized using the campaign's own prompt, list and model.`}
            />
            <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
              <Button
                variant="ghost"
                size="sm36"
                label={STRINGS.CANCEL}
                onClick={() => setRegenConfirm({ open: false, row: null })}
                disabled={regenLoading}
              />
              <Button
                variant="primary"
                size="sm36"
                label="Regenerate"
                onClick={confirmRegenerateCampaign}
                isLoading={regenLoading}
              />
            </Box>
          </Box>
        </CustomDialog>
      )}

      {/* Create Sequence Modal */}
      {sequenceModalOpen && (
        <CreateCampaignModal
          open={sequenceModalOpen}
          setOpen={setSequenceModalOpen}
          payload={campaignPayload}
          setPayload={setCampaignPayload}
        />
      )}

      {/* Edit Folder Modal */}
      {showFolderEdit && (
        <CreateCommonFolder
          open={showFolderEdit}
          setOpen={setShowFolderEdit}
          type={STRINGS.TYPE_CAMP}
          recordId={editFold?.id}
          nameFold={editFold?.name}
          // view={operationRecordPayload?.permission}
        />
      )}

      {/* Edit Folder Modal */}

      {/* Stats Details Dialog */}
      {statsShowModalOpen && (
        <StatsDialog
          open={statsShowModalOpen}
          setOpen={setStatsModalShowOpen}
          statsPayload={statsPayload}
          showProfileHandle={showProfileHandle}
        />
      )}
      {openProfileDialog && (
        <UserProfileDialog
          open={openProfileDialog}
          setOpen={setOpenProfileDialog}
          email={userEmail}
          campaignId={statsPayload?.row?._id}
        />
      )}
      {showRemoveFromFolder && (
        <RemoveFolder
          open={showRemoveFromFolder}
          setOpen={setShowRemoveFromFolder}
          IDs={paginationSelector?.data?.checkedRow}
          type={"campaignRemove"}
          val={STRINGS.CAMPAIGNS_TEXT}
        />
      )}

      {/* Stats Details Dialog */}

      {/* All Modals */}
    </Box>
  );
};

export default CampaignScreen;
